import { useState } from "react";
import { toast } from "sonner";
import { Lock, KeyRound } from "lucide-react";
import { useMode } from "../../context/ModeContext";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";

export default function PinGate() {
  const { unlock, recover } = useMode();
  const { logout } = useAuth();
  const [pin, setPin] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (useRecovery) {
        await recover(recoveryKey);
        toast.success("Recovered — CEO Mode unlocked");
      } else {
        const mode = await unlock(pin);
        toast.success(`${mode.toUpperCase()} Mode unlocked`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A3F2F] flex items-center justify-center px-6" data-testid="pin-gate">
      <div className="w-full max-w-sm bg-white rounded-md p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)] text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#0A3F2F]/10 text-[#0A3F2F] flex items-center justify-center mb-4">
          {useRecovery ? <KeyRound className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
        </div>
        <h1 className="font-serif-brand text-2xl font-semibold text-[#1C1C1C]">{useRecovery ? "Recovery Key" : "Enter Access PIN"}</h1>
        <p className="text-[#5C6A63] text-sm mt-1 mb-6">
          {useRecovery ? "Enter your one-time recovery key to unlock CEO Mode." : "CEO, Manager or Staff — your PIN decides what you see."}
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="pin-form">
          {useRecovery ? (
            <input data-testid="recovery-key-input" className="w-full border border-[#E5E7E5] rounded-md px-4 py-3 text-center tracking-widest font-mono uppercase focus:ring-2 focus:ring-[#0A3F2F]/50 focus:outline-none"
              placeholder="RECOVERY KEY" value={recoveryKey} onChange={(e) => setRecoveryKey(e.target.value)} />
          ) : (
            <input data-testid="pin-input" type="password" inputMode="numeric" maxLength={4} autoFocus
              className="w-full border border-[#E5E7E5] rounded-md px-4 py-3 text-center text-3xl tracking-[0.5em] focus:ring-2 focus:ring-[#0A3F2F]/50 focus:outline-none"
              placeholder="••••" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          )}
          <button type="submit" disabled={loading || (!useRecovery && pin.length !== 4)} data-testid="pin-submit-btn"
            className="w-full bg-[#0A3F2F] text-white font-semibold py-3 rounded-full text-sm hover:bg-[#0d5540] disabled:opacity-50">
            {loading ? "Checking…" : "Unlock"}
          </button>
        </form>
        <button onClick={() => setUseRecovery(!useRecovery)} data-testid="toggle-recovery-btn"
          className="text-xs text-[#5C6A63] hover:text-[#0A3F2F] mt-5 underline underline-offset-4">
          {useRecovery ? "← Back to PIN entry" : "Forgot PIN? Use recovery key"}
        </button>
        <button onClick={logout} data-testid="pin-logout-btn" className="block mx-auto text-xs text-[#5C6A63]/70 hover:text-red-500 mt-3">
          Log out of admin account
        </button>
      </div>
    </div>
  );
}
